import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Grid,
  Avatar,
  Divider,
  Box,
  CircularProgress,
} from "@mui/material";
import {
  AccessTime,
  Event,
  Label,
  Person,
  CheckCircle,
  HourglassEmpty,
  PauseCircle,
  RadioButtonUnchecked,
  FiberManualRecord,
} from "@mui/icons-material";

const statusMap = {
  1: {
    label: "Not Started",
    icon: <RadioButtonUnchecked color="disabled" />,
    color: "disabled",
  },
  2: {
    label: "In Progress",
    icon: <HourglassEmpty color="info" />,
    color: "info",
  },
  3: {
    label: "On Hold",
    icon: <PauseCircle color="warning" />,
    color: "warning",
  },
  4: {
    label: "Completed",
    icon: <CheckCircle color="success" />,
    color: "success",
  },
};

const blockStatusColors = {
  Active: "success.main",
  Inactive: "error.main",
};

const blockStatusMap = {
  1: "Active",
  2: "Inactive",
};

const TicketCard = ({
  blockTitle,
  trailTitle,
  longText,
  reminders,
  eta,
  tags,
  owners,
  taskStatus,
  createdAt,
  doneAt,
  updatedAt,
  blockStatus,
  creator,
  isOwnersLoading,
}) => {
  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderLeft: `4px solid ${blockStatus === 1 ? "#4caf50" : "#f44336"}`,
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack spacing={2.5}>
          {/* Header Section */}
          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              display: "flex",
              alignItems: "center",
            }}
          >
            <FiberManualRecord
              sx={{
                color: blockStatusColors[blockStatusMap[blockStatus]],
                fontSize: 14,
                mr: 1,
              }}
            />
            <Typography variant="body2">
              {blockStatusMap[blockStatus]}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="h6"
              fontWeight="bold"
              color="text.primary"
              sx={{ mb: 0.5 }}
            >
              {blockTitle}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: "flex", alignItems: "center" }}
            >
              {trailTitle}
            </Typography>
          </Box>

          {/* Description */}
          <Box
            sx={{
              backgroundColor: "action.hover",
              p: 2,
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.primary">
              {longText}
            </Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* Reminders Section */}
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 1,
                fontWeight: "medium",
              }}
            >
              <Event fontSize="small" sx={{ mr: 1 }} />
              Reminders
            </Typography>
            <Stack spacing={1} sx={{ pl: 1 }}>
              {reminders ? (
                reminders
                  .map((reminder) => {
                    const match = reminder.match(
                      /\((\d+),(\d{4})(\d{2})(\d{2})\d{4},([^)]+)\)/
                    );
                    if (!match) return null;

                    const [, order, year, month, day, text] = match;
                    return {
                      order: parseInt(order, 10),
                      date: `${year}-${month}-${day}`,
                      text,
                    };
                  })
                  .filter(Boolean)
                  .sort((a, b) => a.order - b.order)
                  .map((reminder, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        pl: 1.5,
                        py: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: "primary.main",
                          mr: 1.5,
                        }}
                      />
                      <Typography variant="body2">
                        <Box
                          component="span"
                          fontWeight="medium"
                          sx={{ mr: 1 }}
                        >
                          {reminder.date}
                        </Box>
                        {reminder.text}
                      </Typography>
                    </Box>
                  ))
              ) : (
                <Typography variant="body2" color="text.disabled">
                  No reminders set
                </Typography>
              )}
            </Stack>
          </Box>

          {/* ETA Section */}
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 1,
                fontWeight: "medium",
              }}
            >
              <AccessTime fontSize="small" sx={{ mr: 1 }} />
              Estimated Time of Arrival
            </Typography>
            <Box
              sx={{
                pl: 3.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              {eta ? (
                <Typography variant="body2">
                  {new Date(eta).toLocaleString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  })}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.disabled">
                  Not specified
                </Typography>
              )}
            </Box>
          </Box>

          {/* Tags Section */}
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{
                mb: 1,
                fontWeight: "medium",
              }}
            >
              <Label fontSize="small" sx={{ mr: 1, verticalAlign: "middle" }} />
              Search Tags
            </Typography>
            <Box sx={{ pl: 3.5 }}>
              {tags ? (
                <Chip
                  label={tags}
                  size="small"
                  sx={{
                    backgroundColor: "primary.light",
                    color: "primary.contrastText",
                  }}
                />
              ) : (
                <Typography variant="body2" color="text.disabled">
                  No tags available
                </Typography>
              )}
            </Box>
          </Box>

          {/* Owners Section */}
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{
                mb: 1,
                fontWeight: "medium",
              }}
            >
              <Person
                fontSize="small"
                sx={{ mr: 1, verticalAlign: "middle" }}
              />
              Block Owners
            </Typography>
            <Box sx={{ pl: 3.5 }}>
              {isOwnersLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading owners...</Typography>
                </Box>
              ) : owners && owners.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {owners.map((owner, index) => (
                    <Chip
                      key={`${owner}-${index}`}
                      label={owner}
                      avatar={
                        <Avatar sx={{ width: 24, height: 24 }}>
                          {owner[0]}
                        </Avatar>
                      }
                      variant="outlined"
                      size="small"
                      sx={{ mb: 0.5 }}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.disabled">
                  No owners assigned
                </Typography>
              )}
            </Box>
          </Box>

          {/* Status Section */}
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{
                mb: 1,
                fontWeight: "medium",
              }}
            >
              Task Status
            </Typography>
            <Box
              sx={{
                pl: 3.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              {statusMap[taskStatus]?.icon || (
                <RadioButtonUnchecked color="disabled" />
              )}
              <Typography variant="body2" sx={{ ml: 1 }}>
                {statusMap[taskStatus]?.label || "Unknown Status"}
              </Typography>
            </Box>
          </Box>

          {/* Date Grid Section */}
          <Box
            sx={{
              backgroundColor: "background.paper",
              p: 1.5,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Created
                </Typography>
                <Typography variant="body2">
                  {createdAt
                    ? new Date(createdAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "N/A"}
                </Typography>
                <Typography variant="caption">
                  {createdAt
                    ? new Date(createdAt).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : ""}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Done
                </Typography>
                <Typography variant="body2">
                  {doneAt
                    ? new Date(doneAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "N/A"}
                </Typography>
                <Typography variant="caption">
                  {doneAt
                    ? new Date(doneAt).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : ""}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Updated
                </Typography>
                <Typography variant="body2">
                  {updatedAt
                    ? new Date(updatedAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "N/A"}
                </Typography>
                <Typography variant="caption">
                  {updatedAt
                    ? new Date(updatedAt).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : ""}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Footer Section */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 1,
            }}
          >
            {isOwnersLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="caption" color="text.secondary">
                  Loading creator...
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center" }}
              >
                Created by: {creator}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default TicketCard;
